/**
 * PurchaseRequestService
 * Service for PurchaseRequest entity operations
 * Route: api/purchaserequest
 * Controller: PurchaseRequestController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * PurchaseRequestService
 * Handles purchase request CRUD operations and custom endpoints
 * Maps to /api/purchaserequest endpoint
 */
export class PurchaseRequestService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PurchaseRequestService.DEFAULT_SERVICE_PATH,
            basePath: PurchaseRequestService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Submit a purchase request
     * PUT /api/purchaserequest/{id}/submit
     * @param id - Purchase request ID
     * @returns Updated purchase request
     */
    async submit(id) {
        const response = await this.submitRaw(id);
        if (!response.data) {
            throw new Error(`Failed to submit purchase request with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Submit purchase request (raw)
     * @param id - Purchase request ID
     * @returns ResponseData with updated purchase request
     */
    submitRaw(id) {
        const endpoint = this.getEndpoint('submit', `${id}/submit`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, {});
    }
    /**
     * Approve a purchase request
     * PUT /api/purchaserequest/{id}/approve
     * @param id - Purchase request ID
     * @returns Updated purchase request
     */
    async approve(id) {
        const response = await this.approveRaw(id);
        if (!response.data) {
            throw new Error(`Failed to approve purchase request with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Approve purchase request (raw)
     * @param id - Purchase request ID
     * @returns ResponseData with updated purchase request
     */
    approveRaw(id) {
        const endpoint = this.getEndpoint('approve', `${id}/approve`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, {});
    }
    /**
     * Reject a purchase request
     * PUT /api/purchaserequest/{id}/reject
     * @param id - Purchase request ID
     * @param reason - Rejection reason
     * @returns Updated purchase request
     */
    async reject(id, reason) {
        const response = await this.rejectRaw(id, reason);
        if (!response.data) {
            throw new Error(`Failed to reject purchase request with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Reject purchase request (raw)
     * @param id - Purchase request ID
     * @param reason - Rejection reason
     * @returns ResponseData with updated purchase request
     */
    rejectRaw(id, reason) {
        const endpoint = this.getEndpoint('reject', `${id}/reject`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, { Reason: reason });
    }
    /**
     * Get purchase request with enriched model view by ID
     * GET /api/purchaserequest/view/{id}
     * @param id - Purchase request ID
     * @returns Enriched purchase request model view
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Purchase request view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get purchase request view by ID (raw)
     * @param id - Purchase request ID
     * @returns ResponseData with enriched model view
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all purchase request views (enriched with lookup data)
     * GET /api/purchaserequest/view
     * @returns Array of enriched purchase request model views
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all purchase request views (raw)
     * @returns ResponseData with array of enriched model views
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('allViews', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered list of purchase request views (enriched with lookup data)
     * POST /api/purchaserequest/list-view
     * @param filter - Optional filter criteria
     * @returns Array of enriched purchase request model views
     */
    async getListView(filter) {
        const response = await this.getListViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered list of purchase request views (raw)
     * @param filter - Optional filter criteria
     * @returns ResponseData with array of enriched model views
     */
    getListViewRaw(filter) {
        const endpoint = this.getEndpoint('listView', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || {});
    }
    /**
     * Get filtered list of purchase request views for current user
     * POST /api/purchaserequest/my-list-view
     * @param filter - Optional filter criteria
     * @returns Array of enriched purchase request model views for current user
     */
    async getMyListView(filter) {
        const response = await this.getMyListViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get my purchase request views (raw)
     * @param filter - Optional filter criteria
     * @returns ResponseData with array of enriched model views
     */
    getMyListViewRaw(filter) {
        const endpoint = this.getEndpoint('myListView', 'my-list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || {});
    }
}
PurchaseRequestService.DEFAULT_SERVICE_PATH = 'procurements';
PurchaseRequestService.BASE_PATH = 'api/purchaserequest';
//# sourceMappingURL=PurchaseRequestService.js.map